import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ComparisonPageData } from '@/lib/directory'
import type { ToolDetail } from '@/types'

import Link from 'next/link'
import { ArrowUpRight, Check, Minus, Trophy } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/directory/page-hero'
import { ComparisonCard } from '@/components/directory/comparison-card'
import { ToolLogo } from '@/components/directory/tool-logo'
import { ScoreBadge, ToolBadges } from '@/components/directory/tool-badges'
import { iconFor } from '@/components/directory/category-icon'
import { getComparisonPage } from '@/lib/directory'

interface ComparisonPageProps {
    data: ComparisonPageData
}

interface Row {
    label: string
    render: (tool: ToolDetail) => ReactNode
}

const nameOf = (tool: ToolDetail) => <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>

const rows: Row[] = [
    {
        label: 'Our score',
        render: tool => tool.score !== null ? <ScoreBadge score={tool.score} /> : <span className='text-muted-foreground'>Not scored</span>
    },
    {
        label: 'Free plan',
        render: tool => tool.is_free ? 'Yes' : 'No'
    },
    {
        label: 'Open source',
        render: tool => tool.is_open_source ? 'Yes' : 'No'
    },
    {
        label: 'Made by',
        render: tool => tool.maker ? <span data-mdlfy-cms={`tools:${tool.id}:maker`}>{tool.maker}</span> : null
    },
    {
        label: 'Best for',
        render: tool => tool.best_for ? <span data-mdlfy-cms={`tools:${tool.id}:best_for`}>{tool.best_for}</span> : null
    },
    {
        label: 'Pricing',
        render: tool => tool.pricing ? <span data-mdlfy-cms={`tools:${tool.id}:pricing`}>{tool.pricing}</span> : null
    },
    {
        label: 'What we like',
        render: tool => tool.pros.length > 0
            ? (
                <ul className='space-y-2'>
                    {tool.pros.map(item => (
                        <li key={item} className='flex gap-2'>
                            <Check className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                            {item}
                        </li>
                    ))}
                </ul>
            )
            : null
    },
    {
        label: 'Watch out for',
        render: tool => tool.cons.length > 0
            ? (
                <ul className='space-y-2'>
                    {tool.cons.map(item => (
                        <li key={item} className='flex gap-2'>
                            <Minus className='mt-0.5 size-4 shrink-0 text-rose-300' aria-hidden='true' />
                            {item}
                        </li>
                    ))}
                </ul>
            )
            : null
    }
]

const ToolHeader: FC<{ tool: ToolDetail }> = ({ tool }): ReactNode => {
    return (
        <div className='flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:p-5'>
            <ToolLogo tool={tool} size='md' />

            <div className='min-w-0 flex-1'>
                <Link href={`/tools/${tool.slug}`} className='font-heading text-lg font-semibold text-foreground hover:text-primary focus-visible:outline-2 focus-visible:outline-primary'>
                    {nameOf(tool)}
                </Link>
                <div className='mt-1'>
                    <ToolBadges tool={tool} />
                </div>
            </div>

            {tool.website_url && !tool.discontinued && (
                <a
                    href={tool.website_url}
                    target='_blank'
                    rel='noopener noreferrer nofollow'
                    aria-label={`Visit the ${tool.name} website (opens in a new tab)`}
                    className='inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-primary'
                >
                    Visit
                    <ArrowUpRight className='size-3.5' aria-hidden='true' />
                </a>
            )}
        </div>
    )
}

const ComparisonPage: FC<ComparisonPageProps> = ({ data }): ReactNode => {
    const { comparison, others } = data
    const { tool_a: a, tool_b: b } = comparison
    const winner = comparison.winner_id === a.id ? a : comparison.winner_id === b.id ? b : null

    return (
        <Layout>
            <Seo
                title={`${a.name} vs ${b.name}`}
                description={comparison.summary ?? `${a.name} vs ${b.name}: scores, free plans, pros, cons and our verdict.`}
            />

            <PageHero
                breadcrumbs={[
                    { href: '/compare', label: 'Matchups' },
                    { label: <>{nameOf(a)} vs {nameOf(b)}</> }
                ]}
                icon={iconFor('matchups')}
                title={<>{nameOf(a)} <span className='text-muted-foreground'>vs</span> {nameOf(b)}</>}
                intro={comparison.summary && <p data-mdlfy-cms={`comparisons:${comparison.id}:summary`}>{comparison.summary}</p>}
            />

            <div className='mx-auto max-w-6xl space-y-12 px-4 pt-10 sm:px-6 lg:px-8'>
                <section id='side-by-side' aria-labelledby='side-by-side-heading'>
                    <h2 id='side-by-side-heading' className='sr-only'>Side by side</h2>

                    <div className='grid grid-cols-2 gap-3 md:grid-cols-[180px_1fr_1fr] md:gap-4'>
                        <div className='hidden md:block' />
                        <ToolHeader tool={a} />
                        <ToolHeader tool={b} />
                    </div>

                    <dl className='mt-4 overflow-hidden rounded-2xl border border-border'>
                        {rows.map((row, index) => (
                            <div
                                key={row.label}
                                className={`grid grid-cols-2 gap-x-3 gap-y-2 px-4 py-4 text-sm md:grid-cols-[180px_1fr_1fr] md:gap-4 md:px-5 ${index % 2 === 0 ? 'bg-card' : 'bg-card/50'}`}
                            >
                                <dt className='col-span-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground md:col-span-1 md:pt-0.5'>
                                    {row.label}
                                </dt>
                                <dd className='leading-relaxed text-foreground/90'>{row.render(a) ?? <span className='text-muted-foreground'>Not listed</span>}</dd>
                                <dd className='leading-relaxed text-foreground/90'>{row.render(b) ?? <span className='text-muted-foreground'>Not listed</span>}</dd>
                            </div>
                        ))}
                    </dl>
                </section>

                {(comparison.pick_a || comparison.pick_b) && (
                    <section id='which-to-pick' aria-labelledby='which-heading'>
                        <h2 id='which-heading' className='font-heading text-2xl font-bold tracking-tight text-foreground'>
                            Which one should you pick?
                        </h2>

                        <div className='mt-5 grid gap-4 md:grid-cols-2'>
                            {[
                                { tool: a, text: comparison.pick_a, column: 'pick_a' },
                                { tool: b, text: comparison.pick_b, column: 'pick_b' }
                            ].map(item => item.text && (
                                <div key={item.column} className='rounded-2xl border border-border bg-card p-6'>
                                    <div className='flex items-center gap-3'>
                                        <ToolLogo tool={item.tool} size='sm' />
                                        <h3 className='font-heading text-lg font-semibold text-foreground'>
                                            Pick {nameOf(item.tool)} if
                                        </h3>
                                    </div>
                                    <p className='mt-3 leading-relaxed text-foreground/85' data-mdlfy-cms={`comparisons:${comparison.id}:${item.column}`}>
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {comparison.verdict && (
                    <section id='verdict' aria-labelledby='verdict-heading' className='rounded-3xl border border-primary/30 bg-primary/5 p-6 sm:p-8'>
                        <div className='flex flex-wrap items-center justify-between gap-4'>
                            <h2 id='verdict-heading' className='font-heading text-2xl font-bold tracking-tight text-foreground'>
                                Our verdict
                            </h2>

                            {winner && (
                                <p className='inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground'>
                                    <Trophy className='size-4' aria-hidden='true' />
                                    Our pick: {nameOf(winner)}
                                </p>
                            )}
                        </div>

                        <p className='mt-4 max-w-3xl text-lg leading-relaxed text-foreground/90' data-mdlfy-cms={`comparisons:${comparison.id}:verdict`}>
                            {comparison.verdict}
                        </p>

                        <div className='mt-6 flex flex-wrap gap-3'>
                            <Link href={`/alternatives/${a.slug}`} className='rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-primary'>
                                {nameOf(a)} alternatives
                            </Link>
                            <Link href={`/alternatives/${b.slug}`} className='rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-primary'>
                                {nameOf(b)} alternatives
                            </Link>
                        </div>
                    </section>
                )}

                {others.length > 0 && (
                    <section id='more-matchups' aria-labelledby='more-heading'>
                        <h2 id='more-heading' className='font-heading text-2xl font-bold tracking-tight text-foreground'>
                            More matchups
                        </h2>
                        <div className='mt-5 grid gap-4 sm:grid-cols-2'>
                            {others.map(other => (
                                <ComparisonCard key={other.id} comparison={other} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps<ComparisonPageProps, { slug: string }> = async ({ params }) => {
    const data = await getComparisonPage(params?.slug ?? '')

    if (!data) return { notFound: true, revalidate: 60 }

    return { props: { data }, revalidate: 60 }
}

export default ComparisonPage
